import React, { memo, useMemo, useState } from 'react';
import classNames from 'classnames';
import './NTCell.scss';
import { prefixClass } from 'lib/utils';
import FormatAccounts from './FormatAccounts';
import FormatReportValues from './FormatReportValues';
import { Amounts, ReportRow } from 'types/report';
import { useNTSelectionContext } from '../NTSelectionContext';
import { useNTContext } from '../NTContext';

const UNEXPECTED_CELL_DATA_WARNING = 'Unexpected cellData type:';
interface NTCellProps {
  cellData: any;
  rowID: string | null;
  cellID: string;
  allColumn: any;
  liveColumn?: boolean;
  totalColumn?: boolean;
  columnIndex?: number;
}

const NTCell = ({ cellData, rowID, cellID, allColumn, totalColumn, liveColumn, columnIndex }: NTCellProps) => {
  const prefix = prefixClass('nt-cell');

  const MemoizedFormatAccounts = useMemo(() => memo(FormatAccounts), []);
  const MemoizedFormatReportValues = useMemo(() => memo(FormatReportValues), []);

  const { dismissedAudit, setDismissedAudit, filters, budgetAvailable } = useNTContext();

  const { cellStates, setCellStates, updateSelectionHistory, transformedData } = useNTSelectionContext();

  const { vizHelpers } = cellData;
  const { noValuestoShow } = vizHelpers;
  const [rowId] = useState(noValuestoShow ? null : rowID);
  const content = useMemo(() => {
    return () => {
      if (!cellData) return '';

      switch (typeof cellData) {
        case 'string':
          return cellData;
        case 'number':
          return formatNumber(cellData);
        case 'object':
          return renderObject(cellData);
        default:
          console.warn(`${UNEXPECTED_CELL_DATA_WARNING} ${typeof cellData}`);
          return String(cellData);
      }
    };
  }, [cellData]);

  const formatNumber = (num: number) => num.toFixed(2);

  const renderObject = (obj: Amounts | ReportRow) => {
    if ('amountTitle' in obj)
      return <MemoizedFormatAccounts cellData={obj} cellID={cellID} allColumn={allColumn} rowID={rowId} />;
    return (
      <MemoizedFormatReportValues
        cellData={obj}
        cellID={cellID}
        rowID={rowId}
        totalColumn={totalColumn}
        transformedData={transformedData}
        columnIndex={columnIndex}
      />
    );
  };

  const handleSelect = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    // Create a new state object to track changes
    const newCellStates = { ...cellStates };
    let stateChanged = false;

    // Handle row selection
    if (cellStates.selected !== rowId) {
      const elementsSelected = document.querySelectorAll(`[data-row-id="${cellStates.selected}"]`);
      elementsSelected.forEach(el => el.classList.remove('selected'));
      const elementsNew = document.querySelectorAll(`[data-row-id="${rowId}"]`);
      elementsNew.forEach(el => el.classList.add('selected'));
      newCellStates.selected = rowID !== cellStates.selected ? rowID : null;
      stateChanged = true;
      if (rowID) updateSelectionHistory(rowID);
    }

    // Handle column selection if columnIndex is provided
    if (columnIndex !== undefined) {
      // Use columnIndex directly - it represents the actual rendered column position
      if (cellStates.selectedColumn !== columnIndex) {
        newCellStates.selectedColumn = columnIndex;
        stateChanged = true;
      }
    }
    const tempDismissedAudit = {
      rowIndex: rowID ? parseInt(rowID.split('_')[0]) : 0,
      columnIndex: columnIndex || 0,
      isDismissed: false,
    };

    // Check if this audit entry already exists to avoid duplicates
    const existingAuditIndex = dismissedAudit.findIndex(
      da => da.rowIndex === tempDismissedAudit.rowIndex && da.columnIndex === tempDismissedAudit.columnIndex
    );

    // Only add if it doesn't exist
    if (existingAuditIndex === -1) {
      setDismissedAudit([...dismissedAudit, tempDismissedAudit]);
    }
    // Only update state if something changed
    if (stateChanged) {
      setCellStates(newCellStates);
    }
  };

  const handleMouseEnter = () => {
    const elements = document.querySelectorAll(`[data-row-id="${rowId}"]`);
    elements.forEach(el => el.classList.add('hovered'));
  };

  const handleMouseLeave = () => {
    const elements = document.querySelectorAll(`[data-row-id="${rowId}"]`);
    elements.forEach(el => el.classList.remove('hovered'));
    //if (cellStates.hover === rowId) setCellStates({ ...cellStates, hover: null });
  };

  // Determine if this cell is in the selected row
  const isInSelectedRow = cellStates.selected === rowId;

  // Determine if this cell is in the selected column
  const isInSelectedColumn = cellStates.selectedColumn === columnIndex;

  // Determine if this is the intersection cell (selected row and column)
  const isIntersectionCell = isInSelectedRow && isInSelectedColumn;

  // Determine if this cell has audit results
  const hasAuditResult = cellData?.auditResult != null;

  // Determine audit result type for styling
  const auditResultType = cellData?.auditResult?.type;
  const getAuditClass = () => {
    if (!hasAuditResult) return '';
    const accountId = rowId?.split('_')[0];
    const dismissedAuditFiltered = dismissedAudit.find(
      da => da.rowIndex === (rowId ? parseInt(rowId.split('_')[0]) : -1) && da.columnIndex === (columnIndex || -1)
    );
    if (dismissedAudit && accountId && dismissedAuditFiltered?.isDismissed) {
      return 'audit-dismissed';
    }

    switch (auditResultType) {
      case 'Error':
      case 'Fail':
        return 'audit-error';
      case 'Warning':
        return 'audit-warning';
      case 'Dismissed':
        return 'audit-dismissed';
      default:
        return '';
    }
  };

  const isTitleCell = !!cellData?.amountTitle;

  return (
    <div
      key={cellID}
      className={classNames(prefix(), {
        live: liveColumn,
        selected: isInSelectedRow, // Apply selected class for row selection
        'selected-cell': isIntersectionCell, // Apply special class for intersection cell
        [getAuditClass()]: hasAuditResult,
        'budget-cell-just-money': budgetAvailable && filters.money && !filters.percentage && !isTitleCell,
        'budget-cell-just-percentage': budgetAvailable && filters.percentage && !filters.money && !isTitleCell,
        'just-percentage-budget-not-available':
          !isTitleCell && !budgetAvailable && filters.percentage && !filters.money,
        'just-money-budget-not-available': !isTitleCell && !budgetAvailable && !filters.percentage && filters.money,
      })}
      onClick={handleSelect}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-cell-id={cellID}
      data-row-id={rowId}
      data-column-id={columnIndex}
    >
      {content()}
    </div>
  );
};
// Use forwardRef directly on the component definition
export default React.memo(
  React.forwardRef<HTMLDivElement, NTCellProps>((props, ref) => {
    return NTCell(props);
  })
);
